import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { ScoreGaugeView } from '@euler/app/components/score/ScoreGauge';
import { calculateScoreViewDimension } from '@euler/app/components/score/util';
import { Typography } from '@euler/app/components/typography';
import { AddProductButton } from '@euler/app/flows/products/components/AddProductButton';
import { ProductItemCell } from '@euler/app/flows/products/components/ProductItemCell';
import { DefectiveLabel } from '@euler/app/flows/report/components/DefectiveLabel';
import {
  InspectionResultCellModel,
  InspectionResultCellType,
} from '@euler/app/flows/report/functions';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { Center } from '@euler/components/Center';
import { Collapsible } from '@euler/components/collapsible';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import {
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelTextShort,
} from '@euler/functions/getDefectiveLevel';
import { deriveManifestItemAdopter } from '@euler/functions/product/deriveManifestItemAdopter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import { DefectiveLevel } from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { assertNever, idEquals } from '@euler/utils';
import {
  useCreation,
  useLocalObservable,
  usePersistFn,
} from '@euler/utils/hooks';
import { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import { memo, useCallback, useMemo } from 'react';
import {
  ListRenderItem,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Svg, { Path } from 'react-native-svg';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';

const kSectionContentSpacing = 15;
const kSectionMargin = 15;

export const InspectionResultsView = memo(
  ({
    cells,
    bottomSheet,
    scoreStyle,
    groupHeaderStyle,
    sectionStyle,
    productManifest,
    ...scrollViewProps
  }: {
    cells: InspectionResultCellModel[];
    productManifest?: ProductManifestProps;
    faultLikeSubject?: FaultInspectionItem;
    bottomSheet?: boolean;
    scoreStyle?:
      | StyleProp<ViewStyle>
      | ((
          group: InspectionResultCellType<'score'>,
        ) => StyleProp<ViewStyle> | undefined);
    groupHeaderStyle?:
      | StyleProp<ViewStyle>
      | ((
          group: InspectionResultCellType<'group-header'>,
        ) => StyleProp<ViewStyle> | undefined);
    sectionStyle?:
      | StyleProp<ViewStyle>
      | ((
          group: InspectionResultCellType<'section'>,
        ) => StyleProp<ViewStyle> | undefined);
  } & ScrollViewProps) => {
    const defaultProductManifest$ = useCreation(
      () => new BehaviorSubject<TaskProductManifestItem[]>([]),
      [],
    );
    const productManifestItems = useLocalObservable(
      productManifest?.items$ ?? defaultProductManifest$,
      [],
    );

    const renderCell: ListRenderItem<InspectionResultCellModel> = useCallback(
      ({ item }) => {
        const type = item.type;
        switch (type) {
          case 'score': {
            const style =
              typeof scoreStyle === 'function' ? scoreStyle(item) : scoreStyle;
            return <ScoreCell score={item.score} key={item.id} style={style} />;
          }

          case 'group-header': {
            const style =
              typeof groupHeaderStyle === 'function'
                ? groupHeaderStyle(item)
                : groupHeaderStyle;
            return <GroupHeaderCell model={item} key={item.id} style={style} />;
          }

          case 'section': {
            const style =
              typeof sectionStyle === 'function'
                ? sectionStyle(item)
                : sectionStyle;
            return (
              <SectionCell
                model={item}
                key={item.id}
                style={style}
                canAssociateProduct={
                  productManifest?.canAssociateProduct &&
                  item.defectiveLevel !== DefectiveLevel.Fine
                }
                productManifestItems={productManifestItems.filter(
                  x => x.subject === item.title,
                )}
                onAddProduct={productManifest?.onAdd}
                onRemoveProduct={productManifest?.onRemove}
                onViewProduct={productManifest?.onViewDetail}
              />
            );
          }

          default:
            assertNever(type);
        }
      },
      [
        groupHeaderStyle,
        productManifest?.canAssociateProduct,
        productManifest?.onAdd,
        productManifest?.onRemove,
        productManifest?.onViewDetail,
        productManifestItems,
        scoreStyle,
        sectionStyle,
      ],
    );

    const ListComponent = bottomSheet ? BottomSheetFlatList : FlatList;

    return (
      <ListComponent
        {...scrollViewProps}
        data={cells}
        keyExtractor={x => x.id}
        renderItem={renderCell}
      />
    );
  },
);

const ScoreCell = memo(
  ({ score, style }: { score: number; style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    const layout = useContainerDimensions();
    const dimens = calculateScoreViewDimension(layout.width, layout.width);
    const width = Math.round(dimens.width - rpx(30));
    const height = Math.round(dimens.height - rpx(30));
    return (
      <View
        css={`
          background-color: #fff;
          padding: ${rpx(15)}px;
          margin-top: ${rpx(10)}px;
        `}
        style={style}
      >
        <ScoreGaugeView score={score} width={width} height={height} />
      </View>
    );
  },
);

const GroupHeaderCell = memo(
  ({
    model,
    style,
  }: {
    model: InspectionResultCellType<'group-header'>;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const color = getDefectiveLevelColor(theme, model.defectiveLevel);
    return (
      <View
        css={`
          padding: ${rpx(15)}px;
          background-color: #fff;
          margin-top: ${rpx(15)}px;
        `}
        style={style}
      >
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Medium};
              font-size: ${rpx(20)}px;
              color: ${color};
            `}
          >
            {model.title}
          </Text>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Regular};
              font-size: ${rpx(18)}px;
              color: ${color};
            `}
          >
            {model.count}项
          </Text>
        </View>
        <Text
          numberOfLines={0}
          css={`
            font-family: ${FontFamily.NotoSans.Light};
            font-size: ${rpx(14)}px;
            color: ${Colors.Gray2};
            line-height: ${rpx(18)}px;
            margin-top: ${rpx(10)}px;
          `}
        >
          {model.description}
        </Text>
      </View>
    );
  },
);

const Triangle = memo(
  ({
    height,
    color,
    style,
  }: {
    height: number;
    color: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const width = (height / 2) * Math.tan(Math.PI / 4.5);
    const d = ['M0,0', `L0,${height}`, `L${width},${height / 2}`, 'Z'].join(
      ' ',
    );
    return (
      <Svg
        css={`
          height: ${height}px;
          width: ${width}px;
          flex-shrink: 0;
          flex-grow: 0;
        `}
        viewBox={`0 0 ${width} ${height}`}
        style={style}
      >
        <Path d={d} fill={color} />
      </Svg>
    );
  },
);

const SectionHeader = memo(
  ({
    model,
    color,
    style,
    titleStyle,
    canAssociateProduct,
    onAssociateProductPress,
  }: {
    model: InspectionResultCellType<'section'>;
    canAssociateProduct?: boolean;
    onAssociateProductPress?: (subject: string) => void;
    color: string;
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const handleAssociateProductPress = usePersistFn(() => {
      onAssociateProductPress?.(model.title);
    });
    return (
      <HStack
        css={`
          flex: 1;
        `}
        justifyContent="space-between"
        alignItems="center"
        style={style}
      >
        <HStack
          css={`
            flex: 1;
          `}
          alignItems="center"
          justifyContent="flex-start"
        >
          <Text
            css={`
              color: ${color};
              left: ${rpx(-3)}px;
            `}
            style={titleStyle}
          >
            <Text
              css={`
                font-weight: bold;
                font-style: italic;
                font-size: ${rpx(28)}px;
              `}
            >
              {model.index + 1}.{' '}
            </Text>
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Regular};
                font-size: ${rpx(16)}px;
                color: ${color};
              `}
            >
              {model.title}
            </Text>
          </Text>
          {model.tag ? (
            <Center
              style={{
                borderColor: color,
                borderWidth: 1,
                borderRadius: rpx(3),
                paddingHorizontal: rpx(3),
                marginLeft: rpx(6),
                height: rpx(16),
                top: rpx(5),
              }}
            >
              <Typography.Text size={rpx(10)} color={color}>
                {model.tag}
              </Typography.Text>
            </Center>
          ) : null}
        </HStack>
        {canAssociateProduct ? (
          <AddProductButton
            onPress={handleAssociateProductPress}
            css={`
              margin-left: ${rpx(10)}px;
              margin-right: ${rpx(3)}px;
              top: ${rpx(2.5)}px;
            `}
          />
        ) : null}
      </HStack>
    );
  },
);

const SectionContent = memo(
  ({
    model,
    color,
    productManifestItems,
    onViewProduct,
    onRemoveProduct,
  }: {
    model: InspectionResultCellType<'section'>;
    color: string;
    productManifestItems?: TaskProductManifestItem[];
    onAddProduct?: (subject: string) => void;
    onRemoveProduct?: (item: TaskProductManifestItem) => void;
    onViewProduct?: (item: TaskProductManifestItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const { width: containerWidth } = useContainerDimensions();
    const contentWidth =
      containerWidth -
      rpx(kSectionMargin) * 2 -
      rpx(kSectionContentSpacing) * 2;
    const mediaHeight = contentWidth * 0.75;

    return (
      <>
        {model.medias.length ? (
          <View
            css={`
              padding: 0 ${rpx(kSectionContentSpacing)}px;
              margin-top: ${rpx(10)}px;
            `}
          >
            <MediaCarousel
              medias={model.medias}
              noPlaceholder
              display="swiper"
              css={`
                width: ${contentWidth}px;
                height: ${mediaHeight}px;
              `}
            />
          </View>
        ) : null}
        {productManifestItems?.length ? (
          <View
            css={`
              padding: 0 ${rpx(kSectionContentSpacing)}px;
              margin-top: ${rpx(10)}px;
            `}
          >
            {productManifestItems.map(item => (
              <ProductItemCell
                item={item}
                key={item.id}
                name={item.product.name}
                title={item.product.title}
                qty={item.qty}
                coverUrl={item.product.coverUrl}
                showBadge={true}
                removable={!item.jobRefs?.length}
                hideQty
                adopted={
                  item.jobRefs?.length
                    ? true
                    : model.productConfirmations?.find(
                        x =>
                          model.title === x.subject &&
                          idEquals(
                            x.contractId,
                            item.product.sourceContractId,
                          ) &&
                          idEquals(x.productId, item.product.id),
                      )?.adopted
                }
                author={item.author}
                adopter={deriveManifestItemAdopter(item)}
                closer={item.closer ?? undefined}
                onRemove={onRemoveProduct}
                onPress={onViewProduct}
                css={`
                  margin: ${rpx(3)}px 0;
                `}
              />
            ))}
          </View>
        ) : undefined}
        {model.items.map((item, index) => (
          <View
            key={index}
            css={`
              flex-direction: row;
              flex-wrap: nowrap;
              align-items: flex-start;
              justify-content: space-between;
              padding: ${rpx(8)}px ${rpx(kSectionContentSpacing)}px;
            `}
          >
            <Triangle
              height={rpx(12)}
              color={color}
              css={`
                top: ${rpx(5)}px;
              `}
            />
            <View
              css={`
                flex: 1;
                margin-left: ${rpx(4)}px;
              `}
            >
              <View
                css={`
                  flex-direction: row;
                  align-items: center;
                `}
              >
                <View
                  css={`
                    flex: 1;
                  `}
                >
                  <Text numberOfLines={0}>
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Regular};
                        font-size: ${rpx(14)}px;
                        color: #000;
                      `}
                    >
                      {`${item.name}: `}
                    </Text>
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Regular};
                        font-size: ${rpx(14)}px;
                        color: ${color};
                      `}
                      style={{
                        textDecorationStyle: 'solid',
                        textDecorationLine: 'underline',
                        textDecorationColor: color,
                      }}
                    >
                      {item.label}
                    </Text>
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Light};
                        color: #888;
                        font-size: ${rpx(12)}px;
                      `}
                    >
                      {item.valueRange ? ' [检测值]' : ''}
                    </Text>
                  </Text>
                </View>
                <DefectiveLabel
                  color={getDefectiveLevelColor(theme, item.defectiveLevel)}
                  text={getDefectiveLevelTextShort(item.defectiveLevel)}
                />
              </View>
              {item.valueRange != null && (
                <View
                  css={`
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-start;
                    padding: ${rpx(4)}px 0;
                  `}
                >
                  <Text
                    css={`
                      color: ${color};
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: ${rpx(13)}px;
                    `}
                  >
                    检测结果评估:{' '}
                  </Text>
                  <Text
                    css={`
                      color: ${color};
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: ${rpx(13)}px;
                    `}
                  >
                    {item.valueRange}
                  </Text>
                </View>
              )}
              {item.description ? (
                <View
                  css={`
                    margin-top: ${rpx(3)}px;
                  `}
                >
                  <Text
                    css={`
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: ${rpx(12)}px;
                      color: ${Colors.Gray2};
                    `}
                  >
                    {item.description}
                  </Text>
                </View>
              ) : null}
              {item.remark?.trim() ? (
                <View
                  css={`
                    margin-top: ${rpx(5)}px;
                  `}
                >
                  <Text
                    css={`
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: ${rpx(12)}px;
                      color: ${Colors.Gray2};
                    `}
                  >
                    备注: {`“${item.remark.trim()}”`}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>
        ))}
      </>
    );
  },
);

const SectionCell = memo(
  ({
    model,
    style,
    ...productManifestProps
  }: {
    model: InspectionResultCellType<'section'>;
    style?: StyleProp<ViewStyle>;
    canAssociateProduct?: boolean;
    productManifestItems?: TaskProductManifestItem[];
    onAddProduct?: (subject: string) => void;
    onRemoveProduct?: (item: TaskProductManifestItem) => void;
    onViewProduct?: (item: TaskProductManifestItem) => void;
  }) => {
    const theme = useTheme();
    const styles = useStyles();
    const { rpx } = useResponsiveContext();

    const color = getDefectiveLevelColor(theme, model.defectiveLevel);
    const bgColor = getDefectiveLevelBgColor(theme, model.defectiveLevel);

    const header = useMemo(
      () => (
        <SectionHeader
          model={model}
          color={color}
          canAssociateProduct={productManifestProps.canAssociateProduct}
          onAssociateProductPress={productManifestProps.onAddProduct}
        />
      ),
      [
        model,
        color,
        productManifestProps.canAssociateProduct,
        productManifestProps.onAddProduct,
      ],
    );

    return (
      <Collapsible.Panel
        header={header}
        caretColor={color}
        caretSize={rpx(24)}
        headerContainerStyle={styles.sectionHeaderContainer}
        contentContainerStyle={styles.contentContainer}
        caretStyle={styles.sectionHeaderCaret}
        style={[styles.collapsiblePanel, style]}
        css={`
          background-color: ${bgColor};
        `}
      >
        <SectionContent model={model} color={color} {...productManifestProps} />
      </Collapsible.Panel>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        sectionHeaderContainer: {
          paddingLeft: rpx(kSectionContentSpacing),
          paddingRight: rpx(kSectionContentSpacing),
          alignItems: 'center',
        },
        contentContainer: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        sectionHeaderCaret: {
          top: rpx(3),
        },
        collapsiblePanel: {
          marginLeft: rpx(kSectionMargin),
          marginRight: rpx(kSectionMargin),
          marginTop: rpx(kSectionMargin),
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: rpx(5),
          paddingBottom: rpx(10),
          borderRadius: rpx(5),
        },
      }),
    [rpx],
  );
};
